import { Alert, Button, Columns, FormField, Input, Section, Select, Stack } from '@ui';
import { useState, type FormEvent, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { errorCode, formErrors } from '../../../lib/errors';
import {
  useUpdateBusiness,
  useUpdateBusinessSettings,
  type Business,
  type UpdateBusinessRequest,
} from '../api';
import styles from './BusinessProfileForm.module.css';

export type ProfileSection = 'basics' | 'address' | 'contact' | 'official' | 'bank' | 'region';

const COUNTRIES = ['DE', 'AT', 'CH', 'LU', 'NL', 'BE', 'FR', 'IT', 'PL', 'CZ', 'DK'] as const;

interface FormState {
  name: string;
  street: string;
  postal_code: string;
  city: string;
  country: string;
  email: string;
  phone: string;
  vet_registration_number: string;
  tax_number: string;
  vat_id: string;
  account_holder: string;
  iban: string;
  bic: string;
  bank_name: string;
  timezone: string;
}

function fromBusiness(t: Business): FormState {
  return {
    name: t.name,
    street: t.address?.street ?? '',
    postal_code: t.address?.postal_code ?? '',
    city: t.address?.city ?? '',
    country: t.address?.country || 'DE',
    email: t.email ?? '',
    phone: t.phone ?? '',
    vet_registration_number: t.vet_registration_number ?? '',
    tax_number: t.tax_number ?? '',
    vat_id: t.vat_id ?? '',
    account_holder: t.bank?.account_holder ?? '',
    iban: t.bank?.iban ?? '',
    bic: t.bank?.bic ?? '',
    bank_name: t.bank?.bank_name ?? '',
    timezone: t.settings.timezone,
  };
}

/** European time zones offered in the selection. */
function timezones(current: string): string[] {
  const all =
    typeof Intl.supportedValuesOf === 'function' ? Intl.supportedValuesOf('timeZone') : [];
  const european = all.filter((z) => z.startsWith('Europe/'));
  const list = european.length > 0 ? european : ['Europe/Berlin', 'Europe/Vienna', 'Europe/Zurich'];
  return list.includes(current) ? list : [current, ...list];
}

const blankToNull = (v: string) => (v.trim() === '' ? null : v);

function toRequest(f: FormState): UpdateBusinessRequest {
  const hasAddress = [f.street, f.postal_code, f.city].some((v) => v.trim() !== '');
  const hasBank = f.iban.trim() !== '' || f.account_holder.trim() !== '';
  return {
    name: f.name,
    address: hasAddress
      ? { street: f.street, postal_code: f.postal_code, city: f.city, country: f.country }
      : null,
    email: blankToNull(f.email),
    phone: blankToNull(f.phone),
    vet_registration_number: blankToNull(f.vet_registration_number),
    tax_number: blankToNull(f.tax_number),
    vat_id: blankToNull(f.vat_id),
    bank: hasBank
      ? {
          account_holder: f.account_holder,
          iban: f.iban,
          bic: blankToNull(f.bic),
          bank_name: blankToNull(f.bank_name),
        }
      : null,
  };
}

export interface BusinessProfileFormProps {
  business: Business;
  sections?: ProfileSection[];
  readOnly?: boolean;
  submitLabel?: string;
  /** Extra buttons next to submit (e.g. "Später"). */
  extraActions?: ReactNode;
  onSaved?: (business: Business) => void;
}

/**
 * Master data (BIZ-002) and, with the section `region`, the time zone of the
 * business (BIZ-003). Always sends the complete profile, so sections that are
 * not shown keep their values. Everything is saved with the one submit button
 * (docs/ux-richtlinien.md §6.9).
 */
export function BusinessProfileForm({
  business,
  sections = ['basics', 'address', 'contact', 'official', 'bank'],
  readOnly = false,
  submitLabel,
  extraActions,
  onSaved,
}: BusinessProfileFormProps) {
  const { t } = useTranslation();
  const update = useUpdateBusiness();
  const updateSettings = useUpdateBusinessSettings();
  const pending = update.isPending || updateSettings.isPending;
  const [form, setForm] = useState<FormState>(() => fromBusiness(business));
  const [loadedVersion, setLoadedVersion] = useState(business.version);

  // Adopt fresh server data (e.g. after a conflict) when it changes.
  if (business.version !== loadedVersion && !pending) {
    setLoadedVersion(business.version);
    setForm(fromBusiness(business));
  }

  const set = (key: keyof FormState) => (e: { target: { value: string } }) =>
    setForm((f) => ({ ...f, [key]: e.target.value }));
  const show = (s: ProfileSection) => sections.includes(s);

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    updateSettings.reset();
    update.mutate(
      { body: toRequest(form), version: business.version },
      {
        onSuccess: (saved) => {
          if (!show('region') || form.timezone === saved.settings.timezone) {
            onSaved?.(saved);
            return;
          }
          // Time zone and locale have their own endpoint (BIZ-003).
          updateSettings.mutate(
            {
              body: { timezone: form.timezone, locale: saved.settings.locale },
              version: saved.version,
            },
            { onSuccess: (withRegion) => onSaved?.(withRegion) },
          );
        },
      },
    );
  };

  const error = update.error ?? updateSettings.error;
  const conflict = errorCode(error) === 'version_conflict';
  const errors = formErrors(error, {
    name: ['business_name.'],
    street: ['address.street'],
    postal_code: ['address.postal_code'],
    city: ['address.city'],
    country: ['address.country'],
    email: ['email.'],
    phone: ['phone.'],
    iban: ['iban.'],
    bic: ['bic.'],
    account_holder: ['bank.account_holder'],
  });
  const field = (
    key: keyof FormState,
    label: string,
    opts: { required?: boolean; autoComplete?: string; type?: string } = {},
  ) => (
    <FormField
      label={label}
      error={errors.field[key as keyof typeof errors.field]}
      {...(opts.required ? { required: true } : {})}
    >
      {(p) => (
        <Input
          {...p}
          type={opts.type ?? 'text'}
          value={form[key]}
          onChange={set(key)}
          readOnly={readOnly}
          {...(opts.autoComplete ? { autoComplete: opts.autoComplete } : {})}
        />
      )}
    </FormField>
  );

  return (
    <Stack as="form" gap={6} onSubmit={onSubmit} noValidate>
      {conflict && <Alert tone="warning">{t('businessSettings.conflict')}</Alert>}
      {errors.general && !conflict && <Alert tone="danger">{errors.general}</Alert>}

      {show('basics') && (
        <Section variant="content" title={t('businessForm.basics')}>
          <div className={styles.name}>
            {field('name', t('businessForm.name'), {
              required: true,
              autoComplete: 'organization',
            })}
          </div>
        </Section>
      )}

      <Columns>
        {show('address') && (
          <Section variant="content" title={t('businessForm.address')}>
            {field('street', t('businessForm.street'), { autoComplete: 'street-address' })}
            <div className={styles.postalRow}>
              {field('postal_code', t('businessForm.postalCode'), { autoComplete: 'postal-code' })}
              {field('city', t('businessForm.city'), { autoComplete: 'address-level2' })}
            </div>
            <FormField label={t('businessForm.country')} error={errors.field.country}>
              {(p) => (
                <Select
                  {...p}
                  value={form.country}
                  onChange={set('country')}
                  disabled={readOnly}
                  options={COUNTRIES.map((c) => ({
                    value: c,
                    label: t(`businessForm.countries.${c}`),
                  }))}
                />
              )}
            </FormField>
          </Section>
        )}

        {show('contact') && (
          <Section variant="content" title={t('businessForm.contact')}>
            <div className={styles.pair}>
              <div className={styles.twoColumns}>
                {field('email', t('businessForm.email'), { type: 'email', autoComplete: 'email' })}
                {field('phone', t('businessForm.phone'), { type: 'tel', autoComplete: 'tel' })}
              </div>
            </div>
          </Section>
        )}

        {show('official') && (
          <Section
            variant="content"
            title={t('businessForm.official')}
            footer={t('businessForm.taxNote')}
          >
            {field('vet_registration_number', t('businessForm.vetRegistrationNumber'))}
            <div className={styles.pair}>
              <div className={styles.twoColumns}>
                {field('tax_number', t('businessForm.taxNumber'))}
                {field('vat_id', t('businessForm.vatId'))}
              </div>
            </div>
          </Section>
        )}

        {show('bank') && (
          <Section variant="content" title={t('businessForm.bank')}>
            {field('account_holder', t('businessForm.accountHolder'))}
            {field('iban', t('businessForm.iban'), { autoComplete: 'off' })}
            <div className={styles.pair}>
              <div className={styles.twoColumns}>
                {field('bic', t('businessForm.bic'))}
                {field('bank_name', t('businessForm.bankName'))}
              </div>
            </div>
          </Section>
        )}

        {show('region') && (
          <Section
            variant="content"
            title={t('businessSettings.title')}
            footer={t('businessSettings.localeAndCurrency')}
          >
            <FormField label={t('businessSettings.timezone')}>
              {(p) => (
                <Select
                  {...p}
                  value={form.timezone}
                  onChange={set('timezone')}
                  disabled={readOnly}
                  options={timezones(business.settings.timezone).map((z) => ({
                    value: z,
                    label: z.replace('Europe/', '').replace('_', ' '),
                  }))}
                />
              )}
            </FormField>
          </Section>
        )}
      </Columns>

      {!readOnly && (
        <Stack direction="row" gap={2} wrap>
          <Button type="submit" loading={pending}>
            {submitLabel ?? t('common.save')}
          </Button>
          {extraActions}
        </Stack>
      )}
    </Stack>
  );
}
